/**
 * GMA-1909 — one place for the idle-session numbers.
 *
 * The caregiver web app shows PHI and runs on shared workstations. A 30-minute
 * inactivity window already existed before this module, in two silent places -
 * AuthContext's `auth_time` poll and a render-time clear inside
 * `useJwtAsState` - and neither warned the user or explained the logout. Both
 * are gone; this module and useSessionCountdown are the single owner.
 *
 * A separate concern often conflated with it: baseApi silently refreshes an
 * expired access TOKEN and retries (GMA-1375). That is token validity, not
 * user inactivity.
 *
 * Both values live here and nowhere else. A literal copied into a component is
 * how the countdown on screen and the logout that fires drift apart.
 */

import { addBreadcrumb } from "./sentry";

/** How long a session survives with no user activity. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

/** How much time is left when the countdown turns red. */
export const SESSION_WARN_MS = 5 * 60 * 1000;

/** Shared across tabs, so activity in one tab resets all of them. */
/**
 * Where the auth token lives.
 *
 * The literal "token" is spelled in 17 files; this does not consolidate them,
 * it names the key for the session machinery, which needs to ask "is there
 * still a session at all" synchronously - a question `expiredRef` cannot
 * answer, because that ref is per-tab and a sign-out in ANOTHER tab clears
 * storage before this tab's storage event has run.
 */
export const AUTH_TOKEN_KEY = "token";

/** How long a sign-out waits for surfaces to tear down before navigating. */
export const SESSION_END_TEARDOWN_MS = 1500;

export const SESSION_DEADLINE_KEY = "cc.session.deadline";

/**
 * Claimed by whichever tab reaches the deadline first, so exactly one logout
 * fires. Every tab holds the same deadline and its own timer, so without this
 * two tabs ticking in the same second both call onExpire.
 */
export const SESSION_EXPIRY_CLAIM_KEY = "cc.session.expiryClaim";

/**
 * The claim is a LEASE, not a permanent grant. A tab can win it and then be
 * closed, crash, or fail its logout before clearing the session — after which
 * every surviving tab would sit on an authenticated session forever if the
 * claim were final. A surviving tab takes over once the lease is stale.
 */
export const SESSION_EXPIRY_CLAIM_LEASE_MS = 10 * 1000;

/**
 * ONE lock name for both halves of the shared-deadline contract: writing a
 * reset, and the expiry check-then-clear. They must exclude each other, or a
 * peer can publish a fresh deadline between the expiry re-read and the clear
 * and everyone is signed out after real activity.
 */
export const SESSION_DEADLINE_LOCK = "cc.session.deadlineLock";

/** Redraw cadence for the clock. */
export const TICK_MS = 1000;

/**
 * A mousemove storm fires hundreds of events a second. Resetting the deadline
 * on every one would write to localStorage just as often, for no extra
 * fidelity — the deadline only needs to be correct to the second.
 */
export const ACTIVITY_THROTTLE_MS = 500;

/** What counts as "the user is still here". */
export const ACTIVITY_EVENTS = [
  // `click` matters on its own: assistive technologies and voice control can
  // synthesize one with no preceding mousedown or keydown, so a user actively
  // driving the app by voice would otherwise be timed out as idle. The legacy
  // AuthContext tracker had it; dropping it here was a real regression.
  "click",
  "mousedown",
  "mousemove",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
];

/**
 * mm:ss, never negative, rounded UP so the final second reads 00:01 rather
 * than sitting on 00:00 for a whole second before the logout fires.
 */
export const formatRemaining = (remainingMs) => {
  const safe = Math.max(0, Number(remainingMs) || 0);
  const totalSeconds = Math.ceil(safe / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/**
 * True only inside the warning band. Expiry is a logout, not a warning — once
 * the clock reaches zero the countdown has no state left to show.
 */
export const isWarning = (remainingMs) =>
  remainingMs > 0 && remainingMs <= SESSION_WARN_MS;

/**
 * Set just before a sign-out so the login page can explain what happened.
 * sessionStorage, not localStorage: the explanation belongs to this tab and
 * this navigation, must not greet the user again tomorrow, and — the reason it
 * matters for issue #672 — must survive the `localStorage.clear()` that the
 * refresh-failure sign-out runs between the mark and the redirect.
 *
 * The key name is historical (GMA-1909 shipped it as an idle-only boolean) and
 * is deliberately unchanged: a tab signed out by the previous build and
 * redirected into this one holds a marker under this exact key.
 */
export const SESSION_END_NOTICE_KEY = "cc.session.idleLogoutNotice";

/** @deprecated the marker now stores a REASON; prefer SESSION_END_NOTICE_KEY. */
export const IDLE_LOGOUT_NOTICE_KEY = SESSION_END_NOTICE_KEY;

/** The 30-minute inactivity timer fired. */
export const SESSION_END_REASON_IDLE = "idle";

/**
 * The session ended without the user asking and without the idle timer: a
 * refresh-token failure, or a token that was simply gone when the tab came
 * back. Issue #672 — every one of these used to land on a bare /login.
 */
export const SESSION_END_REASON_SESSION_ENDED = "session_ended";

/**
 * The closed set. A marker holding anything else is discarded rather than
 * rendered, because a reason with no copy is an empty red box on the login
 * page — worse than the silence this issue is fixing.
 */
export const SESSION_END_REASONS = Object.freeze([
  SESSION_END_REASON_IDLE,
  SESSION_END_REASON_SESSION_ENDED,
]);

export const IDLE_LOGOUT_MESSAGE =
  `You were signed out because there was no activity for ${Math.round(
    SESSION_IDLE_MS / 60000
  )} minutes. Please sign in again.`;

/**
 * Deliberately says nothing about WHY beyond "it ended". The user cannot act
 * on "your refresh token was rejected", and naming a cause we are not certain
 * of is worse than naming none.
 */
export const SESSION_ENDED_MESSAGE =
  "Your session ended and you were signed out. Please sign in again.";

/** Every reason has copy, or it must not be in SESSION_END_REASONS. */
export const SESSION_END_MESSAGES = Object.freeze({
  [SESSION_END_REASON_IDLE]: IDLE_LOGOUT_MESSAGE,
  [SESSION_END_REASON_SESSION_ENDED]: SESSION_ENDED_MESSAGE,
});

/**
 * The pre-#672 marker value, written by any tab still running the old build.
 * It only ever meant the idle timer, so that is what it reads back as.
 */
const LEGACY_IDLE_MARKER = "1";

const normalizeReason = (raw) => {
  if (raw === LEGACY_IDLE_MARKER) return SESSION_END_REASON_IDLE;
  return SESSION_END_REASONS.includes(raw) ? raw : null;
};

/**
 * Record WHY the session ended, for the login page to explain.
 *
 * Issue #672: this was `markIdleLogout()`, an idle-only boolean, so the three
 * other sign-out paths had nothing to write and the caregiver got no
 * explanation at all.
 */
export const markSessionEndReason = (reason) => {
  const normalized = normalizeReason(reason);
  if (!normalized) return false;
  // Drop the per-page memo. A tab that already asked and was told "no notice"
  // caches that answer for the life of the page - and a tab sitting on /login
  // can be authenticated later by another tab's token storage event without
  // ever reloading. When a cross-tab idle logout then marks a notice, the
  // cached false would hide the explanation AND leave the marker behind for
  // some unrelated later page to show. Invalidating (rather than caching the
  // reason) keeps the marker the single source of truth and consumed once.
  sessionEndAnswer = undefined;
  try {
    window.sessionStorage?.setItem(SESSION_END_NOTICE_KEY, normalized);
  } catch (_err) {
    // The notice is a courtesy; never let it break the logout itself.
  }
  try {
    // The confirming filter for #672. The reason string is the ENTIRE payload:
    // no user id, no token, no path — this fires on an auth boundary in a
    // product that shows PHI.
    addBreadcrumb("session.end.reason_marked", {
      category: "session.end.reason_marked",
      level: "info",
      reason: normalized,
    });
  } catch (_err) {
    // Telemetry must never throw out of a sign-out.
  }
  return true;
};

/** GMA-1909's entry point, unchanged for its two call sites. */
export const markIdleLogout = () => markSessionEndReason(SESSION_END_REASON_IDLE);

/**
 * Is a reason already spoken for on this page load — in the memo, or still
 * sitting in storage waiting to be read?
 *
 * NORMALIZED, not merely non-empty (PR #698 review, round 2). A stored value
 * this build does not recognise — a marker written by a newer version, then a
 * rollback — would otherwise count as pending and block a valid mark, and the
 * consumer discards it a moment later. The user then gets no notice at all,
 * which is the exact silence this issue exists to remove.
 */
const hasSessionEndReason = () => {
  if (sessionEndAnswer) return true;
  try {
    const raw = window.sessionStorage?.getItem(SESSION_END_NOTICE_KEY);
    return Boolean(normalizeReason(raw));
  } catch (_err) {
    // Storage unavailable — nothing can be pending in it.
    return false;
  }
};

/**
 * Mark a reason ONLY if none is pending. The first reason wins.
 *
 * Every UNEXPECTED session end goes through this rather than
 * `markSessionEndReason`, because they race with the idle path and would
 * otherwise replace an accurate explanation with the generic one. The concrete
 * race (PR #698 review): the idle timer fires and writes "idle", then a refresh
 * that was already in flight resolves `success: false` while the teardown is
 * still being awaited, and the caregiver is told their session "ended" when in
 * fact they were away for thirty minutes.
 *
 * `markIdleLogout` deliberately does NOT go through here: the inactivity timer
 * is the most specific thing we know about a sign-out, so it is authoritative.
 */
export const markSessionEndReasonIfUnset = (reason) => {
  if (hasSessionEndReason()) return false;
  return markSessionEndReason(reason);
};

/**
 * "Why did THIS page load arrive here?" — answered once and memoised.
 *
 * The answer must not depend on how many times it is asked. Under
 * React.StrictMode every lazy initializer and every mount effect runs twice,
 * and a naive read-and-remove hands the reason to the discarded call and
 * nothing to the committed one — so the explanation silently never appears, in
 * development, which is where anyone would look for it.
 *
 * `undefined` means "not yet asked"; `null` is a real answer meaning "no
 * notice", which is why the memo is not simply nullable.
 */
let sessionEndAnswer = undefined;

export const consumeSessionEndReason = () => {
  if (sessionEndAnswer !== undefined) {
    return sessionEndAnswer;
  }
  let result = null;
  try {
    const raw = window.sessionStorage?.getItem(SESSION_END_NOTICE_KEY);
    if (raw) {
      // Removed even when it does not normalize: an unrecognised marker is
      // never going to render, and leaving it behind would make every later
      // page load re-read it.
      window.sessionStorage.removeItem(SESSION_END_NOTICE_KEY);
      result = normalizeReason(raw);
    }
  } catch (_err) {
    // fall through
  }
  sessionEndAnswer = result;
  return result;
};

/** GMA-1909's boolean, now scoped to the idle reason ONLY — a refresh-token
 *  failure must not render "there was no activity for 30 minutes". */
export const consumeIdleLogoutNotice = () =>
  consumeSessionEndReason() === SESSION_END_REASON_IDLE;

/** Test-only: the memo is per page load, and a test is many page loads. */
export const __resetSessionEndReasonMemoForTests = () => {
  sessionEndAnswer = undefined;
};

/** @deprecated kept for the GMA-1909 tests that already call it. */
export const __resetIdleLogoutMemoForTests = __resetSessionEndReasonMemoForTests;

/**
 * Issue #672 — "did this page load ever hold a session?"
 *
 * The route guards see only `!isLoggedIn`, which covers BOTH a token that
 * vanished mid-session and a visitor who typed a protected URL while signed
 * out. Only the first is an unexplained sign-out; the second is a login page
 * doing its job, and a notice there would be a lie.
 *
 * Module-level rather than a component ref: each protected route mounts its own
 * guard element, so a ref would reset on the very route change that follows the
 * token disappearing.
 */
let hadPriorSession = false;

/**
 * Called while the user IS signed in. Idempotent — a render-time call under
 * StrictMode runs twice, and must mean the same thing both times.
 *
 * Starting a session RETIRES the previous one's answer (PR #698 review). One
 * page load can span two sessions: a tab sitting on /login consumes an "idle"
 * notice, another tab authenticates it by a token storage event with no reload,
 * and that new session later loses its token. Without this reset the memo still
 * holds "idle", which both suppresses the new marker and renders the wrong
 * explanation. The marker itself was already removed from storage when it was
 * consumed, so clearing the memo restores "no answer yet" rather than
 * resurrecting an old notice.
 */
export const notePriorSession = () => {
  if (!hadPriorSession) {
    sessionEndAnswer = undefined;
  }
  hadPriorSession = true;
};

/**
 * A DELIBERATE sign-out is self-explanatory, so it clears the note rather than
 * earning a notice. It does not un-write a reason already marked: the idle path
 * marks "idle" and then calls logout(), and that copy must survive.
 */
export const clearPriorSessionNote = () => {
  hadPriorSession = false;
};

/**
 * #701 — spend the prior-session note and report whether it was set.
 * Unlike `markSessionEndIfPriorSession`, the return value does not depend on
 * whether a reason was written (idle may already own the marker).
 */
export const spendPriorSessionNote = () => {
  if (!hadPriorSession) return false;
  hadPriorSession = false;
  return true;
};

/**
 * Mark `session_ended` if — and only if — this page load actually had a
 * session that has now gone.
 *
 * Never overwrites: the first reason wins. The idle path marks "idle" before
 * the guards run, and clobbering it would replace the accurate copy with the
 * generic one. Returns whether it marked, so a caller can assert it.
 */
export const markSessionEndIfPriorSession = () => {
  if (!hadPriorSession) return false;
  // The noted session is over, whether or not this call is the one that got to
  // name the reason — so the note is spent here (PR #698 review, round 2).
  // Leaving it set means a page later re-authenticated in place by a peer tab
  // sees no transition in `notePriorSession`, keeps the retired session's
  // memoised answer, and suppresses or mislabels the NEXT sign-out. Unlike the
  // idle path, a guard-detected end never passes through `logout()`, so this is
  // the only place that clears it.
  hadPriorSession = false;
  return markSessionEndReasonIfUnset(SESSION_END_REASON_SESSION_ENDED);
};

/** Test-only: the note is per page load, and a test is many page loads. */
export const __resetPriorSessionNoteForTests = () => {
  hadPriorSession = false;
};

/** Minutes, for copy — derived so the words can never drift from the timer. */
export const SESSION_IDLE_MINUTES = Math.round(SESSION_IDLE_MS / 60000);

/** What the badge says it is, when there is plenty of time left. */
export const SESSION_LABEL = "Auto sign-out in";

/** What it says once the clock is red. */
export const SESSION_WARNING_LABEL = "Signing out in";

/** How to stop it, in the user's words. */
export const SESSION_KEEP_ALIVE_HINT =
  "Move the mouse or press a key to stay signed in";

/**
 * What the live region actually announces when the clock turns red.
 *
 * The hint alone ("Move the mouse...") never says WHY, and the clock itself is
 * deliberately not a live region - announcing it would read a new time aloud
 * every second - so without this sentence a screen-reader user is told to act
 * and never told that they are about to be signed out.
 */
// The band, not an instant: `isWarning` is true anywhere in
// (0, SESSION_WARN_MS], and this single constant is what the live region
// announces throughout - a tab that mounts or adopts a shared deadline with two
// minutes left speaks the same sentence as one that has just crossed into the
// band. An exact duration would therefore be false for almost the whole window
// it is spoken in, to the one user who cannot see the clock. It is stated as an
// upper bound so it is true at every point in the band, and it stays non-ticking
// because a live region that re-announced a new time each second would be worse
// than silence.
export const SESSION_WARNING_ANNOUNCEMENT =
  `Automatic sign-out in ${Math.round(SESSION_WARN_MS / 60000)} minutes or less. ` +
  `${SESSION_KEEP_ALIVE_HINT}.`;

/** The full explanation, shown on hover and to screen readers. */
export const SESSION_EXPLANATION =
  `For privacy, you are signed out automatically after ${Math.round(SESSION_IDLE_MS / 60000)} minutes ` +
  "without activity. Moving the mouse, typing or clicking starts the countdown over.";
